import React, { Component } from 'react';
import PropTypes from 'prop-types';
import reduxForm from 'redux-form/es/reduxForm';
import formValueSelector from 'redux-form/es/formValueSelector';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { changeCreditLine, getContractData, getMmps } from 'actions/selfservice';
import { track } from 'actions/analytics';
import InformationStep from 'components/Credit24/SelfService/ChangeCreditLine/InformationStep';
import ConfirmationStep from 'components/Credit24/SelfService/ChangeCreditLine/ConfirmationStep';
import Button from 'components/common/Button';
import ProgressBar from 'components/Credit24/Core/ProgressBar';
import { keyErrorsFieldRequired } from 'utils/helpers';

const propTypes = {
  selfservice: PropTypes.object,
  authentication: PropTypes.object,
  getContractData: PropTypes.func,
  getMmps: PropTypes.func,
  changeCreditLine: PropTypes.func,
  track: PropTypes.func,
  handleSubmit: PropTypes.func,
  submitAgree: PropTypes.bool,
  ASIAKASTIETO_DATAPOOL: PropTypes.bool,
};

class ChangeCreditLineContainer extends Component {
  constructor() {
    super();
    this.state = {
      currentStep: 0,
    };
    this.titles = [
      'selfservice.upgrade.title',
      'selfservice.upgrade.confirmation.title',
    ];
    this.forwardTitles = [
      'selfservice.upgrade.continue',
      'selfservice.upgrade.confirm',
    ];
    this.steps = [
      InformationStep,
      ConfirmationStep,
    ];
    this.progressBarSteps = [
      { type: 'upgradeStart' },
      { type: 'upgradeConfirm' },
    ];

    this.onSubmit = this.onSubmit.bind(this);
  }

  componentDidMount() {
    const { contracts = [] } = this.props.authentication;
    contracts.forEach((contractId) => {
      this.props.getContractData(contractId);
      this.props.getMmps(contractId);
    });

    if (COUNTRY === 'FI') {
      if (this.props.selfservice.isContractDataLoaded) {
        this.props.track(
          'checkoutSelfservice',
          {
            product: 'CL-UPGRADE',
            step: 1,
          }
        );
      }
    }
  }

  componentDidUpdate(prevProps) {
    if (COUNTRY === 'FI') {
      const { selfservice } = this.props;

      if (!prevProps.selfservice.isContractDataLoaded && selfservice.isContractDataLoaded) {
        this.props.track(
          'checkoutSelfservice',
          {
            product: 'CL-UPGRADE',
            step: 1,
          }
        );
      }
    }
  }

  onSubmit(data) {
    const { currentStep } = this.state;
    if (currentStep === 0) {
      this.setState({
        currentStep: currentStep + 1,
      }, () => {
        window.scrollTo(0, 0);
      });
      if (COUNTRY === 'FI') {
        this.props.track(
          'checkoutSelfservice',
          {
            product: 'CL-UPGRADE',
            step: 2,
          }
        );
      }
    } else {
      const {
        authentication,
        selfservice,
      } = this.props;
      const contractId = authentication.contracts[0];
      const contract = selfservice.contracts[contractId] || {};
      const { id } = contract.upgrade.product;
      if (COUNTRY === 'FI') {
        this.props.changeCreditLine(
          contractId,
          id,
          data,
          [{
            consentName: 'ASIAKASTIETO_DATAPOOL',
            version: 'FI_FI_C24_DPDDatapool_20180725',
          }]
        );
      } else {
        this.props.changeCreditLine(contractId, id);
      }
    }
  }

  render() {
    const { currentStep } = this.state;
    const {
      authentication,
      selfservice,
      handleSubmit,
      submitAgree,
      ASIAKASTIETO_DATAPOOL,
    } = this.props;
    const contractId = authentication.contracts[0];
    const contract = selfservice.contracts[contractId] || {};

    const { upgrade } = contract;

    if (!contract.id || !(upgrade && upgrade.product)) {
      return null;
    }

    const Step = this.steps[currentStep];
    const submitDisabled = currentStep !== 0
      && (!submitAgree || (COUNTRY === 'FI' && !MOBILE_WALLET && !ASIAKASTIETO_DATAPOOL) || selfservice.isLoading);

    return (
      <>
        <div className="container">
          <FormattedMessage id={this.titles[currentStep]}>
            {(title) => (<h1 className="page-title">{title}</h1>)}
          </FormattedMessage>
        </div>
        <div className="container container--mobile-fullWidth">
          <form onSubmit={handleSubmit(this.onSubmit)}>
            <Step upgrade={upgrade} authentication={authentication} />
            <nav className="step-navigation">
              <div className="step-navigation__back">
                <Button type="button" btnType="secondary" tag="link" to="/dashboard" isFullWidth>
                  <FormattedMessage id="selfservice.upgrade.cancel" />
                </Button>
              </div>
              <div className="step-navigation__content">
                <ProgressBar
                  visibleSteps={this.progressBarSteps}
                  progressIndex={currentStep}
                />
              </div>
              <div className="step-navigation__forward">
                <Button
                  type="submit"
                  isLoading={currentStep !== 0 && selfservice.isLoading}
                  disabled={submitDisabled}
                  isFullWidth
                >
                  <FormattedMessage id={this.forwardTitles[currentStep]} />
                </Button>
              </div>
            </nav>
          </form>
        </div>
      </>
    );
  }
}

ChangeCreditLineContainer.propTypes = propTypes;

const upgradeFormConfig = {
  form: 'upgradeForm',
  touchOnChange: true,
  validate: (values) => {
    const errors = {};
    if (!values.ASIAKASTIETO_DATAPOOL) {
      errors.ASIAKASTIETO_DATAPOOL = 'consent.ASIAKASTIETO_DATAPOOL.warning';
    }
    if (!values.submitAgree) {
      errors.submitAgree = keyErrorsFieldRequired;
    }
    return errors;
  },
  onSubmitFail: (error) => {
    if (DEBUG) {
      console.warn('form submit fail', error);
    }
  },
};

const selector = formValueSelector('upgradeForm');

export default reduxForm(upgradeFormConfig)(connect(
  (state) => ({
    selfservice: state.selfservice,
    authentication: state.authentication,
    submitAgree: selector(state, 'submitAgree'),
    ASIAKASTIETO_DATAPOOL: selector(state, 'ASIAKASTIETO_DATAPOOL'),
  }),
  {
    getContractData,
    getMmps,
    changeCreditLine,
    track,
  }
)(ChangeCreditLineContainer));
